import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild, inject, HostListener } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { BackOfficePermissions, PermissionGroup, PermissionsDetails } from './model/UserGroupDetails';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Group } from '../user/model/group';
import { UserManagementState } from '../../../../shared/state/UserManagementState';
import { Store } from '@ngxs/store';
import { DealerState, UpdateGroupsAction } from '../../../../shared/state/DealerState';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { UserManagementService } from '../../services/user-management.service';
import { UserFunctionLink } from './model/UserFunction';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { Alert, AlertService, AlertType, ModalComponent } from '@michelin/theme';
import { UserGroupModel } from './model/user-group.model';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { UtilityService } from '../../../../shared/services/utility.service';
import { UserTabService } from '../../services/user-tab.service';
import {
  backOfficePermissions,
  functionList,
  generalPermissions,
  permissionGroupList,
  shopFloorPermissions
} from '../shared/user-group-permissions';
import { UnsavedChangesService } from '../../../../shared/services/unsaved-changes.service';
import { ADMIN_GROUP_NAME } from '../../../../shared/constants/constants';

@Component({
  selector: 'app-user-group',
  templateUrl: './user-group.component.html',
  styleUrl: './user-group.component.scss',
  standalone: false
})
export class UserGroupComponent implements OnInit, CanComponentDeactivate, OnDestroy {
  lastUpdatedBy: string;
  createdBy: string;
  lastUpdatedTimestamp: string;
  createdTimestamp: string;
  group: Group;
  dealer: Dealer;
  groupPermissionsLoading = false;
  groupDesc: string;
  @Input() groupName = '';
  @Input() groupId = '';

  permissionGroupList: PermissionGroup[] = permissionGroupList;
  shopFloorPermissions: PermissionsDetails[] = shopFloorPermissions;
  generalPermissions: PermissionsDetails[] = generalPermissions;
  backOfficePermissions: BackOfficePermissions[] = backOfficePermissions;
  functionList: UserFunctionLink[] = functionList;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;
  initialFormValues: any;
  existingPermissions: string[] = [];
  isEmptyUserGroup = false;

  saving = false;

  public alertService = inject(AlertService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  protected translationService = inject(TranslationService);
  private readonly activatedRoute = inject(ActivatedRoute);
  protected userManagementService = inject(UserManagementService);
  private readonly datePipe = inject(DatePipe);
  private readonly store = inject(Store);
  private utilityService = inject(UtilityService);
  private userTabService = inject(UserTabService);
  private unsavedChangesService = inject(UnsavedChangesService);
  private router = inject(Router);

  constructor() {
    this.permissionGroupList = [
      { label: 'shopfloorPostsPermissionsText', permissions: this.shopFloorPermissions },
      { label: 'generalPermissionsText', permissions: this.generalPermissions }
    ];
    this.groupName = this.activatedRoute.snapshot.paramMap.get('group-name');
  }

  userGroupForm = this.formBuilder.group({
    userGroupName: ['', Validators.compose([Validators.required])],
    userGroupDesc: ['', Validators.compose([])],
    filterPermission: ['', Validators.compose([])]
  });

  ngOnInit(): void {
    // Block access to ADMIN group detail/edit screen
    if (this.groupName?.toUpperCase() === ADMIN_GROUP_NAME) {
      this.router.navigate(['home/setup/usersetup/user-group']);
      return;
    }
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.fetchGroupDetails();
    this.userGroupForm.patchValue({
      userGroupName: this.groupName,
      userGroupDesc: this.groupDesc
    });
    this.initialFormValues = this.userGroupForm.value;
    this.unsavedChangesService.register(this);
  }

  fetchGroupDetails(): void {
    const groups = this.store.selectSnapshot(UserManagementState.currentPaginatedGroups);
    this.group = groups.find((group) => group.groupName === this.groupName);

    if (!this.group) {
      return;
    }

    this.groupName = this.group.groupName;
    this.groupId = this.group.userGroupId;
    this.groupDesc = this.group.description;
    this.lastUpdatedBy = this.group.lastModifiedBy;
    this.createdBy = this.group.createdBy ? this.group.createdBy : this.translationService.translate('defaultCreatedText');

    this.groupPermissionsLoading = true;

    this.userManagementService
      .getUserGroupDetails(this.dealer.id, this.groupId)
      .pipe(
        map((response) => {
          if (!response) {
            return;
          }

          this.functionList = response.userFunctionLink;
          const keyList = this.getAllPermissionKeys();
          this.existingPermissions = this.getAllPermissionKeys();

          return {
            backOffice: this.processPermissionsAsync(this.backOfficePermissions, keyList, 'BACK_OFFICE'),
            shopFloor: this.processPermissionsAsync(this.shopFloorPermissions, keyList, null),
            general: this.processPermissionsAsync(this.generalPermissions, keyList, null)
          };
        }),
        catchError((error) => {
          console.error('Error fetching group details:', error);
          return of(null);
        }),
        finalize(() => {
          this.groupPermissionsLoading = false;
        })
      )
      .subscribe((processes) => {
        if (processes) {
          forkJoin([processes.backOffice, processes.shopFloor, processes.general]).subscribe(() => {
            this.groupPermissionsLoading = false;
          });
        }
      });

    const formatUtcDate = (timestamp: string | undefined): string => {
      return this.datePipe.transform(timestamp, "yyyy/MM/dd 'at' hh:mm a", 'UTC');
    };
    this.lastUpdatedTimestamp = formatUtcDate(this.group.lastUpdtTimestamp);
    this.createdTimestamp = formatUtcDate(
      this.group.createdTimestamp ? this.group.createdTimestamp : this.translationService.translate('defaultCreatedTimeText')
    );
  }

  private processPermissionsAsync(permissions: any[], keyList: string[], classification: string | null) {
    return new Promise<void>((resolve) => {
      permissions.forEach((permission) => {
        this.functionList.forEach((userFunctionLink) => {
          if (!classification || userFunctionLink.classification === classification) {
            if (userFunctionLink.functionName === permission.leftSideLabelKey) {
              permission.isCheckedLeftBox = true;
            }
            if (userFunctionLink.functionName === permission.rightSideLabelKey) {
              permission.isCheckedRightBox = true;
            }
          }
        });
        this.mapPermissionDetails(permission, keyList);
      });
      resolve();
    });
  }

  private mapPermissionDetails(permission: any, keyList: string[]): void {
    const mappings = [
      { key: 'WORK_ORDER', view: 'WORK_ORDER_VIEW', edit: 'WORK_ORDER_EDIT', create: 'WORK_ORDER_CREATE' },
      { key: 'POS', view: 'VIEW_POS_INTERFACES', edit: 'EDIT_POS_INTERFACES' },
      { key: 'USER_SETUP', view: 'USER_SETUP_VIEW', edit: 'USER_SETUP_EDIT' },
      { key: 'PROFILE_SETUP', all: 'PROFILE_SETUP' },
      { key: 'CUSTOMER', view: 'CUSTOMER_SETUP_VIEW', edit: 'CUSTOMER_SETUP_EDIT' },
      { key: 'SITE_SETUP', view: 'SITE_SETUP_INFO_VIEW', edit: 'SITE_SETUP_INFO_EDIT' },
      { key: 'PLANT_SETUP', view: 'PLANT_SETUP_INFO_VIEW', edit: 'PLANT_SETUP_INFO_EDIT' },
      { key: 'SETUP_SERVICES', view: 'SETUP_SERVICES_VIEW', edit: 'SETUP_SERVICES_EDIT' },
      { key: 'PRODUCT_SETUP', view: 'PRODUCT_SETUP_VIEW', edit: 'PRODUCT_SETUP_EDIT' },
      {
        key: 'SETUP_FLEET_SPECIFICATIONS',
        view: 'SETUP_FLEET_SPECIFICATIONS_VIEW',
        edit: 'SETUP_FLEET_SPECIFICATIONS_EDIT'
      },
      { key: 'SETUP_CASING_GRADE', view: 'SETUP_CASING_GRADE_VIEW', edit: 'SETUP_CASING_GRADE_EDIT' },
      { key: 'NOTIFICATION_HUB', view: 'NOTIFICATION_HUB_VIEW', edit: 'NOTIFICATION_HUB_EDIT' }
    ];

    mappings.forEach((mapping) => {
      if (this.isViewEditCreateEnabled(keyList, mapping.key, permission.leftSideLabelKey)) {
        permission.isViewChecked = this.isPermissionAvailable(keyList, mapping.view);
        permission.isEditChecked = this.isPermissionAvailable(keyList, mapping.edit);
        permission.isAllChecked = this.isPermissionAvailable(keyList, mapping.all);
        permission.isCreateChecked = mapping.create ? this.isPermissionAvailable(keyList, mapping.create) : false;
        if (mapping.key === 'WORK_ORDER') {
          permission.isAllChecked = permission.isViewChecked && permission.isEditChecked && permission.isCreateChecked;
        } else if (mapping.key === 'PROFILE_SETUP' && permission.isAllChecked) {
          permission.isViewChecked = true;
          permission.isEditChecked = true;
        } else {
          permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
        }
      }
    });
  }

  getAllPermissionKeys(): string[] {
    const keys: string[] = [];
    this.functionList.forEach((permission) => {
      keys.push(permission.functionName);
    });
    return keys;
  }

  isViewEditCreateEnabled(permission: string[], key: string, functionKey: string) {
    return this.isPermissionAvailable(permission, key) && functionKey === key;
  }

  isPermissionAvailable(permission: string[], matchVal: string) {
    for (const element of permission) {
      if (element.includes(matchVal)) {
        return true;
      }
    }
    return false;
  }

  // Check if all permissions in a group are checked
  isAllPermissionsChecked(permissionDetails: PermissionsDetails[]): boolean {
    return permissionDetails.every((permission) => permission.isCheckedLeftBox && permission.isCheckedRightBox);
  }

  // Check if all back office permissions are checked
  isAllBackOfficePermissionsChecked(): boolean {
    return this.backOfficePermissions.every(
      (permission) =>
        permission.isAllChecked &&
        permission.isViewChecked &&
        permission.isEditChecked &&
        (permission.id !== 1 || permission.isCreateChecked)
    );
  }

  // Activate all permissions within a category
  activateAllPermissions(permissionDetails: PermissionsDetails[], isChecked: boolean): void {
    permissionDetails.forEach((permission) => {
      permission.isCheckedLeftBox = isChecked;
      permission.isCheckedRightBox = isChecked;
    });
  }

  // Activate all back office permissions
  activateAllBackOfficePermissions(isChecked: boolean): void {
    this.backOfficePermissions.forEach((permission) => {
      permission.isAllChecked = isChecked;
      permission.isViewChecked = isChecked;
      permission.isEditChecked = isChecked;
      if (permission.id === 1) {
        permission.isCreateChecked = isChecked;
      }
    });
  }

  // Event handler for shop floor permissions checkbox state change
  onPermissionChange(permission: PermissionsDetails, isLeftBox: boolean, isChecked: boolean): void {
    if (!isLeftBox) {
      permission.isCheckedRightBox = isChecked;
    } else {
      permission.isCheckedLeftBox = isChecked;
    }
  }

  // Event handler for back office permissions checkbox state change
  onBackOfficePermissionChange(permission: BackOfficePermissions, type: string, isChecked: boolean): void {
    switch (type) {
      case 'all':
        permission.isAllChecked = isChecked;
        permission.isViewChecked = isChecked;
        permission.isEditChecked = isChecked;
        if (permission.id === 1) {
          permission.isCreateChecked = isChecked;
        }
        break;
      case 'view':
        permission.isViewChecked = isChecked;
        permission.isAllChecked =
          permission.isViewChecked && permission.isEditChecked && (permission.id !== 1 || permission.isCreateChecked);
        break;
      case 'edit':
        permission.isEditChecked = isChecked;
        permission.isAllChecked =
          permission.isViewChecked && permission.isEditChecked && (permission.id !== 1 || permission.isCreateChecked);
        break;
      case 'create':
        permission.isCreateChecked = isChecked;
        permission.isAllChecked =
          permission.isViewChecked && permission.isEditChecked && (permission.id !== 1 || permission.isCreateChecked);
        break;
    }
  }

  validateAndSaveUserGroup(): void {
    this.userGroupForm.markAllAsTouched();
    console.log('Changes: ', this.isFormChanged());
    // Check if there are any modifications before saving (only for existing groups)
    if (this.groupId && !this.isFormChanged()) {
      this.utilityService.showAlert(AlertType.strong, '', this.translationService.translate('noModifiedText'));
      return;
    }

    if (this.getSelectedPermissionKeys().length <= 0) {
      this.isEmptyUserGroup = true;
      this.utilityService.showAlert(
        AlertType.strong,
        this.translationService.translate('missingValuesText'),
        this.translationService.translate('atLeastOnePermissionText')
      );
      return;
    } else {
      this.isEmptyUserGroup = false;
    }
    if (this.userGroupForm.valid) {
      this.saving = true;
      const UserGroupUpdateModel: UserGroupModel = {
        userGroupFk: this.groupId,
        userGroupName: this.userGroupForm.get('userGroupName')?.value,
        groupModifier: this.store.selectSnapshot(DealerState.currentUser),
        userGroupDescription: this.userGroupForm.get('userGroupDesc')?.value,
        userFunctionNames: this.getSelectedPermissionKeys()
      };
      if (this.groupId) {
        this.userManagementService.updateUserGroup(this.dealer.id, UserGroupUpdateModel).subscribe({
          next: () => {
            this.saving = false;
            this.lastUpdatedTimestamp = this.formatTimestamp(new Date().toISOString());
            this.existingPermissions = this.getSelectedPermissionKeys();
            this.lastUpdatedBy = UserGroupUpdateModel.groupModifier;
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('camSaveSuccessText')
            } as Alert);
            this.initialFormValues = this.userGroupForm.value;
            this.existingPermissions = this.getSelectedPermissionKeys();
          },
          error: (error) => {
            this.saving = false;
            const errorMessage = error.error ? JSON.stringify(error.error) : error.message ? error.message : error.toString();
            this.alertService.showAlert({
              type: AlertType.critical,
              title: this.translationService.translate('errorText'),
              content: this.translationService.translate('failureUpdateGroupText') + errorMessage
            } as Alert);
          }
        });
      } else {
        this.userManagementService.saveUserGroup(this.dealer.id, UserGroupUpdateModel).subscribe({
          next: (response) => {
            this.saving = false;
            this.initialFormValues = this.userGroupForm.value;
            this.existingPermissions = this.getSelectedPermissionKeys();
            this.lastUpdatedTimestamp = this.formatTimestamp(new Date().toISOString());
            this.createdTimestamp = this.formatTimestamp(new Date().toISOString());
            this.createdBy = UserGroupUpdateModel.groupModifier;
            this.lastUpdatedBy = UserGroupUpdateModel.groupModifier;
            this.utilityService.showAlert(AlertType.success, '', this.translationService.translate('userGroupCreatedSuccessfullyText'));
            this.store.dispatch(new UpdateGroupsAction(null));
            this.groupId = response.data.userGroupId;
          },
          error: (error) => {
            this.saving = false;
            if (error.status === 409) {
              this.userGroupForm.patchValue({
                userGroupName: error.error.groupName,
                userGroupDesc: error.error.groupDescription
              });
              this.activateAllBackOfficePermissions(false);
              this.activateAllPermissions(this.shopFloorPermissions, false);
              this.activateAllPermissions(this.generalPermissions, false);
              this.functionList = error.error.userFunctionLink;
              this.userGroupForm.get('userGroupName').setErrors({ userGroupNameExists: true });
              const keyList = this.getAllPermissionKeys();
              return {
                backOffice: this.processPermissionsAsync(this.backOfficePermissions, keyList, 'BACK_OFFICE'),
                shopFloor: this.processPermissionsAsync(this.shopFloorPermissions, keyList, null),
                general: this.processPermissionsAsync(this.generalPermissions, keyList, null)
              };
            } else {
              this.utilityService.showAlert(AlertType.critical, '', this.translationService.translate('userGroupCreationFailedText'));
            }
          }
        });
      }
    } else {
      this.utilityService.showAlert(
        AlertType.strong,
        this.translationService.translate('incorrectValuesText'),
        this.translationService.translate('incorrectValuesOnUserGroupText')
      );
    }
  }

  getSelectedPermissionKeys(): string[] {
    const selectedPermissions: string[] = [];
    this.shopFloorPermissions.forEach((permission) => {
      if (permission.isCheckedLeftBox && permission.leftSideLabelKey) {
        selectedPermissions.push(permission.leftSideLabelKey);
      }
      if (permission.isCheckedRightBox && permission.rightSideLabelKey) {
        selectedPermissions.push(permission.rightSideLabelKey);
      }
    });
    this.generalPermissions.forEach((permission) => {
      if (permission.isCheckedLeftBox && permission.leftSideLabelKey) {
        selectedPermissions.push(permission.leftSideLabelKey);
      }
      if (permission.isCheckedRightBox && permission.rightSideLabelKey) {
        selectedPermissions.push(permission.rightSideLabelKey);
      }
    });
    this.backOfficePermissions.forEach((permission) => {
      if (permission.isViewChecked && permission.viewKey) {
        selectedPermissions.push(permission.viewKey);
      }
      if (permission.isEditChecked && permission.editKey) {
        selectedPermissions.push(permission.editKey);
      }
      if (permission.isCreateChecked && permission.createKey) {
        selectedPermissions.push(permission.createKey);
      }
    });
    return selectedPermissions;
  }

  private isFormChanged(): boolean {
    const sortedSelectedPermission = [...this.getSelectedPermissionKeys()].sort();
    const sortedExistingPermission = [...this.existingPermissions].sort();
    return (
      JSON.stringify(this.initialFormValues) !== JSON.stringify(this.userGroupForm.value) ||
      JSON.stringify(sortedSelectedPermission) !== JSON.stringify(sortedExistingPermission)
    );
  }

  canDeactivate(): boolean | Promise<boolean> {
    return canDeactivateForm(
      this.isFormChanged.bind(this),
      this.unsavedChangesModal,
      this.unsavedChangesContentModal,
      this.translationService,
      () => {
        this.userTabService.resetTab(1);
        return false;
      }
    );
  }

  private formatTimestamp(timestamp: string): string {
    return this.datePipe.transform(timestamp, "yyyy/MM/dd 'at' hh:mm a", 'UTC');
  }

  ngOnDestroy() {
    this.activateAllBackOfficePermissions(false);
    this.activateAllPermissions(this.shopFloorPermissions, false);
    this.activateAllPermissions(this.generalPermissions, false);
    this.unsavedChangesService.unregister();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isFormChanged()) {
      $event.returnValue = true;
    }
  }
}
